import React, { Component } from 'react'
import styled from 'styled-components'
import { FormGroup, FormControl, ControlLabel, Alert } from 'react-bootstrap'
import {
  getUserTimezone,
  getBrowserTimezone,
  saveUserTimezone,
} from '../../utils/timezoneStorage'

/**
 * Timezone Settings Page
 * Allows users to configure their preferred timezone for date/time display
 * Auto-saves when timezone is changed
 */
class TimezoneSettingsPage extends Component {
  constructor(props) {
    super(props)

    // Get timezone from localStorage or default to browser timezone
    const savedTimezone = getUserTimezone()
    const browserTimezone = getBrowserTimezone()

    this.state = {
      selectedTimezone: savedTimezone,
      browserTimezone: browserTimezone,
      showReloadMessage: false,
    }
  }

  // Get UTC offset for a timezone
  getUTCOffset = (timezone) => {
    try {
      const now = new Date()
      const tzDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }))
      const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }))
      const offsetMinutes = (tzDate - utcDate) / (1000 * 60)
      const offsetHours = offsetMinutes / 60

      const sign = offsetHours >= 0 ? '+' : '-'
      const absHours = Math.abs(Math.floor(offsetHours))
      const minutes = Math.abs(offsetMinutes % 60)

      if (minutes === 0) {
        return `UTC${sign}${absHours}`
      } else {
        return `UTC${sign}${absHours}:${minutes.toString().padStart(2, '0')}`
      }
    } catch (error) {
      return 'UTC'
    }
  }

  // Get all supported timezones from browser
  getTimezones = () => {
    try {
      // Get all supported timezones
      const allTimezones = Intl.supportedValuesOf('timeZone')

      // Map to format with city names and UTC offset
      return allTimezones.map((tz) => {
        // Extract city name from timezone identifier (e.g., "Asia/Tokyo" -> "Tokyo")
        const parts = tz.split('/')
        const cityName = parts[parts.length - 1].replace(/_/g, ' ')
        const offset = this.getUTCOffset(tz)

        return {
          value: tz,
          label: `${cityName} (${offset})`,
        }
      })
      // Sort alphabetically by city name
      .sort((a, b) => a.label.localeCompare(b.label))
    } catch (error) {
      // Fallback if Intl.supportedValuesOf is not available
      console.warn('Intl.supportedValuesOf not supported, using fallback timezones')
      return [
        { value: 'Asia/Bangkok', label: 'Bangkok (UTC+7)' },
        { value: 'Asia/Tokyo', label: 'Tokyo (UTC+9)' },
        { value: 'Asia/Shanghai', label: 'Shanghai (UTC+8)' },
        { value: 'Asia/Hong_Kong', label: 'Hong Kong (UTC+8)' },
        { value: 'Asia/Singapore', label: 'Singapore (UTC+8)' },
        { value: 'Asia/Seoul', label: 'Seoul (UTC+9)' },
        { value: 'Asia/Kuala_Lumpur', label: 'Kuala Lumpur (UTC+8)' },
        { value: 'Asia/Jakarta', label: 'Jakarta (UTC+7)' },
        { value: 'UTC', label: 'UTC (UTC+0)' },
      ]
    }
  }

  timezones = this.getTimezones()

  handleTimezoneChange = (event) => {
    const newTimezone = event.target.value

    // Update state
    this.setState({ selectedTimezone: newTimezone, showReloadMessage: true })

    // Auto-save to localStorage
    saveUserTimezone(newTimezone)

    // Reload page after a short delay to apply the new timezone
    setTimeout(() => {
      window.location.reload()
    }, 1500)
  }

  // Extract city name from timezone identifier
  getCityName = (timezone) => {
    const parts = timezone.split('/')
    return parts[parts.length - 1].replace(/_/g, ' ')
  }

  render() {
    const { selectedTimezone, browserTimezone, showReloadMessage } = this.state
    const browserCityName = this.getCityName(browserTimezone)
    const browserOffset = this.getUTCOffset(browserTimezone)

    return (
      <Container>
        <h3>Timezone Settings</h3>
        <p className="text-muted">
          Select your timezone for displaying dates and times throughout the application.
          Changes are saved automatically.
        </p>

        <FormGroup>
          <ControlLabel>Display Timezone</ControlLabel>
          <FormControl
            componentClass="select"
            value={selectedTimezone}
            onChange={this.handleTimezoneChange}
          >
            <option value={browserTimezone}>
              Browser Default - {browserCityName} ({browserOffset})
            </option>
            <option disabled>──────────</option>
            {this.timezones.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </FormControl>
          <HelpBlock>
            Browser default: {browserCityName} ({browserOffset})
          </HelpBlock>
        </FormGroup>

        {showReloadMessage && (
          <Alert bsStyle="success" style={{ marginTop: '20px' }}>
            <strong>Saved!</strong> Reloading page to apply timezone changes...
          </Alert>
        )}
      </Container>
    )
  }
}

const Container = styled.div`
  padding: 20px;
  max-width: 600px;
`

const HelpBlock = styled.p`
  margin-top: 5px;
  margin-bottom: 10px;
  color: #737373;
  font-size: 12px;
`

export default TimezoneSettingsPage
